// API configuration
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

// Types
export interface User {
  id: string;
  email: string;
  name: string;
}

export interface AuthResponse {
  accessToken: string;
  tokenType: string;
  userId: string;
  email: string;
  name: string;
}

export interface RegisterInput {
  name: string;
  email: string;
  password: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface ForgotPasswordInput {
  email: string;
}

export interface ResetPasswordInput {
  token: string;
  newPassword: string;
}

// API helper functions
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
    throw new Error(error.detail || 'An error occurred');
  }
  return response.json();
}

// Auth API
export const authApi = {
  /**
   * Register a new user
   */
  async register(data: RegisterInput): Promise<AuthResponse> {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return handleResponse<AuthResponse>(response);
  },

  /**
   * Login user
   */
  async login(data: LoginInput): Promise<AuthResponse> {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return handleResponse<AuthResponse>(response);
  },

  /**
   * Request password reset
   */
  async forgotPassword(data: ForgotPasswordInput): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return handleResponse<{ message: string }>(response);
  },

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordInput): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return handleResponse<{ message: string }>(response);
  },

  /**
   * Get current user info
   */
  async getCurrentUser(token: string): Promise<User> {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return handleResponse<User>(response);
  },
};

// Storage helpers for JWT token
export const authStorage = {
  getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
  },

  setToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('auth_token', token);
  },

  removeToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('auth_token');
  },

  getUser(): User | null {
    if (typeof window === 'undefined') return null;
    const userStr = localStorage.getItem('auth_user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  setUser(user: User | null): void {
    if (typeof window === 'undefined') return;
    if (user) {
      localStorage.setItem('auth_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('auth_user');
    }
  },
};

// Meetings API
export interface Meeting {
  id: string;
  title: string;
  description?: string | null;
  roomName: string;
  scheduledAt: string;
  createdBy: string;
  isActive: boolean;
  createdAt: string;
}

export interface MeetingCreateInput {
  title: string;
  description?: string;
  scheduledAt: string;
  createdBy: string;
}

export const meetingsApi = {
  /**
   * Schedule a new meeting
   */
  async schedule(token: string, data: MeetingCreateInput): Promise<Meeting> {
    const response = await fetch(`${API_URL}/meetings/schedule`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return handleResponse<Meeting>(response);
  },

  /**
   * Create an instant meeting
   */
  async createInstant(token: string, createdBy: string, title: string): Promise<Meeting> {
    const params = new URLSearchParams({ created_by: createdBy, title });
    const response = await fetch(`${API_URL}/meetings/instant?${params}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return handleResponse<Meeting>(response);
  },

  /**
   * List meetings for a user
   */
  async list(token: string, createdBy: string): Promise<Meeting[]> {
    const response = await fetch(`${API_URL}/meetings?created_by=${encodeURIComponent(createdBy)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return handleResponse<Meeting[]>(response);
  },

  /**
   * Start a meeting
   */
  async start(token: string, meetingId: string, createdBy: string): Promise<{ status: string; message: string }> {
    const response = await fetch(`${API_URL}/meetings/start/${meetingId}?created_by=${encodeURIComponent(createdBy)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return handleResponse<{ status: string; message: string }>(response);
  },

  /**
   * Delete a meeting
   */
  async delete(token: string, meetingId: string, createdBy: string): Promise<{ status: string; message: string }> {
    const response = await fetch(`${API_URL}/meetings/delete/${meetingId}?created_by=${encodeURIComponent(createdBy)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return handleResponse<{ status: string; message: string }>(response);
  },
};

// Connection API
export interface ConnectionDetails {
  serverUrl: string;
  roomName: string;
  participantName: string;
  participantToken: string;
  iceServers: Array<{
    urls: string[];
    username?: string;
    credential?: string;
  }>;
}

export const connectionApi = {
  /**
   * Get connection details for a room
   */
  async getConnectionDetails(roomName: string, token: string): Promise<ConnectionDetails> {
    const response = await fetch(
      `${API_URL}/connection-details?roomName=${encodeURIComponent(roomName)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return handleResponse<ConnectionDetails>(response);
  },
};

export { API_URL };
